<script lang="ts" setup>
import { useHead } from "@unhead/vue"
import { useUrlSearchParams } from "@vueuse/core"
import { computed, provide } from "vue"
import { useRouter } from "vue-router"
import { useGetAlbum } from "@/api"
import { AlbumDeleteModal, AlbumSettingsGeneral, AlbumSettingsPrivacy } from "@/components"
import { Button, Card, TabControl } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useConvertedRouteParam, useModal, useTranslation } from "@/composables"
import { SidebarHostEventLayout } from "@/layouts"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { AlbumDeleteModalKey } from "@/symbols"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const eventStore = useEventVaultStore()

const albumId = useConvertedRouteParam("albumId", "/event/[id]/album/[albumId]/edit", value => Number(value))
const router = useRouter()

const { success, error } = useToast()

provide("albumId", albumId)

const { t } = useTranslation()

useHead({ title: t("album_settings.heading") })

const queryParams = useUrlSearchParams<{ tab?: string }>()

const activeAlbumSettingsTab = computed({
  get: () => {
    if (queryParams.tab === "privacy")
      return "privacy"
    if (queryParams.tab === "danger-zone")
      return "danger-zone"
    return "general"
  },
  set: (value: string) => { queryParams.tab = value },
})

const albumSettingsTabs = computed(() => [
  { id: "general", label: t("album_settings.general.label") },
  { id: "privacy", label: t("album_settings.privacy.label") },
  { id: "danger-zone", label: t("album_settings.danger_zone.label") },
])

const { toggle } = useModal(AlbumDeleteModalKey)

const { data: album, isLoading } = useGetAlbum(eventStore.eventVaultId, albumId)

function onSettingsSuccess() {
  success({
    title: t("album_settings.success_title"),
    description: t("album_settings.success_content"),
    icon: "CircleCheck",
  })

  router.push(`/event/${eventStore.eventVaultId}/albums`)
}

function onSettingsError() {
  error()
}

function onAlbumDeleteSuccess() {
  router.push(`/event/${eventStore.eventVaultId}/albums`)
}
</script>

<template>
  <SidebarHostEventLayout>
    <h1 class="font-heading font-bold text-2xl mb-8">
      {{ t("album_settings.heading") }}
    </h1>
    <TabControl
      v-model="activeAlbumSettingsTab"
      :tabs="albumSettingsTabs"
      :tablist-aria-label="t('album_settings.heading')"
    />

    <div class="mt-4">
      <AlbumSettingsGeneral
        v-if="activeAlbumSettingsTab === 'general'"
        @success="onSettingsSuccess"
        @error="onSettingsError"
      />
      <AlbumSettingsPrivacy
        v-if="activeAlbumSettingsTab === 'privacy'"
        @success="onSettingsSuccess"
        @error="onSettingsError"
      />
      <Card v-if="activeAlbumSettingsTab === 'danger-zone' && album" variant="danger">
        <div class="flex flex-col gap-4">
          <div>
            <h3 class="text-xl font-heading font-bold text-theme-danger">
              {{ t("album_settings.general.delete_title") }}
            </h3>
          </div>
          <p class="text-sm md:text-base text-label">
            {{ t("album_settings.general.delete_description") }}
          </p>
          <div>
            <Button variant="danger" size="sm" icon="Delete" class="w-full md:w-auto" data-test="delete-album" @click.prevent="toggle">
              {{ t("album_settings.general.delete_button") }}
            </Button>
          </div>
        </div>
      </Card>
    </div>

    <AlbumDeleteModal :album="album" :is-loading="isLoading" @success="onAlbumDeleteSuccess" />
  </SidebarHostEventLayout>
</template>
